<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>

    <admin-breadcrumb
        :items="[{ text: 'Reports', path: { name: 'admin-reports' } }]"
        name="Account Billing Status"
        :title="pageTitle"
    />

    <admin-panel with-header>
      <template #header>
        <div class="account-billing-header">
          <div class="account-billing-filters">
            <div class="account-billing-filter">
              <label for="billing-status-filter">Billing Status</label>
              <b-form-select
                  id="billing-status-filter"
                  v-model="filters.billing_status"
                  :options="billingStatusOptions"
                  @change="onFiltersChanged"
              />
            </div>

            <div class="account-billing-filter">
              <label for="state-filter">State</label>
              <b-form-select
                  id="state-filter"
                  v-model="filters.state"
                  :options="stateOptions"
                  @change="onFiltersChanged"
              />
            </div>

            <div class="account-billing-filter account-billing-filter--search">
              <label for="company-search">Company / Account</label>
              <b-form-input
                  id="company-search"
                  v-model.trim="filters.search"
                  placeholder="Search by company name or account ID"
                  @input="onSearchInput"
              />
            </div>
          </div>

          <div class="account-billing-actions">
            <b-btn
                variant="primary"
                class="mr-2"
                v-b-tooltip.hover
                title="Export CSV"
                :disabled="!rows.length"
                @click.prevent="downloadCsv"
            >
              <b-icon-download/>
            </b-btn>

            <b-btn
                variant="primary"
                v-b-tooltip.hover
                title="Reload Report"
                @click.prevent="loadReport"
            >
              <b-icon-arrow-clockwise/>
            </b-btn>
          </div>
        </div>
      </template>

      <div class="text-muted mb-3">
        One row per company account with its current billing and subscription status.
      </div>

      <b-table
          :fields="columns"
          :items="rows"
          table-class="text-nowrap"
          responsive
          hover
          show-empty
          empty-text="No accounts found"
      >
        <template #cell(company_name)="data">
          <div class="company-name-cell">
            {{ data.item.company_name }}
          </div>
        </template>

        <template
            v-for="column in sortableColumns"
            #[`head(${column.key})`]
        >
          <button
              :key="`sort-${column.key}`"
              type="button"
              class="report-sort-head btn btn-link p-0 d-inline-flex align-items-center"
              @click="toggleSort(column.key)"
          >
            <span>{{ column.label }}</span>
            <span class="ml-2">{{ sortIcon(column.key) }}</span>
          </button>
        </template>

        <template #cell(account_type)="data">
          <span class="text-capitalize">
            {{ formatAccountType(data.item.account_type) }}
          </span>
        </template>

        <template #cell(billing_status)="data">
          <span
              class="billing-status-badge"
              :class="getBillingStatusClass(data.item.billing_status)"
          >
            {{ formatBillingStatus(data.item.billing_status) }}
          </span>
        </template>

        <template #cell(subscription_start_date)="data">
          {{ formatDate(data.item.subscription_start_date) }}
        </template>

        <template #cell(most_recent_payment_date)="data">
          {{ formatDate(data.item.most_recent_payment_date) }}
        </template>

        <template #cell(next_payment_date)="data">
          {{ formatDate(data.item.next_payment_date) }}
        </template>
      </b-table>
    </admin-panel>
  </b-container>
</template>

<script>
import moment from "moment";
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminPanel from "../components/AdminPanel";
import {API_ROUTES} from "../api";

const SORTABLE_COLUMN_KEYS = [
  "company_name",
  "billing_status",
  "subscription_start_date",
  "most_recent_payment_date",
  "next_payment_date",
];

export default {
  name: "AdminReportAccountBillingStatus",

  components: {
    AdminPanel,
    AdminBreadcrumb,
    AdminPreloader,
  },

  data() {
    return {
      loading: false,
      searchTimer: null,
      reportRows: [],
      availableStates: [],
      filters: {
        billing_status: null,
        state: null,
        search: "",
        sort: "company_name",
        sort_direction: "asc",
      },
      columns: [
        {
          key: "company_name",
          label: "Company Name",
          thClass: "border-top-0 company-name-column",
          tdClass: "align-middle company-name-column"
        },
        {key: "account_id", label: "Account ID", thClass: "border-top-0", tdClass: "align-middle"},
        {key: "state", label: "State", thClass: "border-top-0", tdClass: "align-middle"},
        {key: "account_type", label: "Account Type", thClass: "border-top-0", tdClass: "align-middle"},
        {key: "billing_status", label: "Billing Status", thClass: "border-top-0", tdClass: "align-middle"},
        {
          key: "subscription_start_date",
          label: "Subscription Start Date",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
        {
          key: "most_recent_payment_date",
          label: "Most Recent Payment Date",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
        {
          key: "next_payment_date",
          label: "Next Payment Date",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
      ],
    };
  },

  computed: {
    pageTitle() {
      return "Account Billing Status";
    },

    rows() {
      return this.reportRows.map((item) => ({
        company_name: item?.company_name || "Unknown",
        account_id: item?.account_id ?? item?.company_id ?? "-",
        state: item?.state || "-",
        account_type: item?.account_type || "-",
        billing_status: this.normalizeBillingStatus(item?.billing_status),
        subscription_start_date: item?.subscription_start_date || null,
        most_recent_payment_date: item?.most_recent_payment_date || null,
        next_payment_date: item?.next_payment_date || null,
      }));
    },

    sortableColumns() {
      return this.columns.filter((column) => SORTABLE_COLUMN_KEYS.includes(column.key));
    },

    billingStatusOptions() {
      return [
        {value: null, text: "All Billing Statuses"},
        {value: "paid", text: "Paid"},
        {value: "free", text: "Free"},
        {value: "in_default", text: "In Default"},
      ];
    },

    stateOptions() {
      return [
        {value: null, text: "All States"},
        ...this.availableStates.map((state) => ({
          value: state,
          text: state,
        })),
      ];
    },
  },

  methods: {
    getRequestParams() {
      const params = {
        sort: this.filters.sort,
        sort_direction: this.filters.sort_direction,
      };

      if (this.filters.billing_status) {
        params.billing_status = this.filters.billing_status;
      }

      if (this.filters.state) {
        params.state = this.filters.state;
      }

      if (this.filters.search) {
        params.search = this.filters.search;
      }

      return params;
    },

    loadReport() {
      this.loading = true;

      this.$api.get(API_ROUTES.ADMIN_REPORT_ACCOUNT_BILLING_STATUS, {
        params: this.getRequestParams(),
      })
          .then(({data}) => {
            this.reportRows = Array.isArray(data?.report_data)
                ? data.report_data
                : [];

            this.availableStates = Array.isArray(data?.filters?.states)
                ? data.filters.states
                : this.extractStates(this.reportRows);
          })
          .catch(({response}) => {
            this.reportRows = [];

            const message = response?.status === 422
                ? "Some report filters are invalid."
                : "Failed to load account billing status report.";

            this.$bvToast.toast(message, {
              title: "Error!",
              variant: "danger",
            });
          })
          .finally(() => {
            this.loading = false;
          });
    },

    onFiltersChanged() {
      this.loadReport();
    },

    onSearchInput() {
      window.clearTimeout(this.searchTimer);

      this.searchTimer = window.setTimeout(() => {
        this.loadReport();
      }, 400);
    },

    toggleSort(column) {
      if (!SORTABLE_COLUMN_KEYS.includes(column)) {
        return;
      }

      if (this.filters.sort === column) {
        this.filters.sort_direction =
            this.filters.sort_direction === "asc" ? "desc" : "asc";
      } else {
        this.filters.sort = column;
        this.filters.sort_direction = "asc";
      }

      this.loadReport();
    },

    sortIcon(column) {
      if (this.filters.sort !== column) {
        return "↕";
      }

      return this.filters.sort_direction === "asc" ? "↑" : "↓";
    },

    extractStates(items) {
      return [...new Set(
          items
              .map((item) => item?.state)
              .filter(Boolean)
      )].sort();
    },

    normalizeBillingStatus(status) {
      const normalized = String(status || "")
          .trim()
          .toLowerCase()
          .replace(/[\s-]+/g, "_");

      if (["paid", "free", "in_default"].includes(normalized)) {
        return normalized;
      }

      return normalized || "unknown";
    },

    formatBillingStatus(status) {
      switch (this.normalizeBillingStatus(status)) {
        case "paid":
          return "Paid";
        case "free":
          return "Free";
        case "in_default":
          return "In Default";
        default:
          return "Unknown";
      }
    },

    getBillingStatusClass(status) {
      return `billing-status-badge--${this.normalizeBillingStatus(status)}`;
    },

    formatAccountType(value) {
      if (!value) {
        return "-";
      }

      return String(value).replace(/_/g, " ");
    },

    formatDate(value) {
      if (!value) {
        return "-";
      }

      const date = moment(value);

      return date.isValid() ? date.format("L") : "-";
    },

    escapeCsvValue(value) {
      return `"${String(value ?? "").replace(/"/g, '""')}"`;
    },

    getCsvValue(row, key) {
      if (key === "billing_status") {
        return this.formatBillingStatus(row[key]);
      }

      if (key === "account_type") {
        return this.formatAccountType(row[key]);
      }

      if (key.includes("date")) {
        return row[key] ? moment(row[key]).format("YYYY-MM-DD") : "";
      }

      return row[key] ?? "";
    },

    downloadCsv() {
      const headers = this.columns.map((column) =>
          this.escapeCsvValue(column.label)
      );

      const dataRows = this.rows.map((row) =>
          this.columns.map((column) =>
              this.escapeCsvValue(this.getCsvValue(row, column.key))
          )
      );

      const csv = [
        headers.join(","),
        ...dataRows.map((row) => row.join(",")),
      ].join("\n");

      const blob = new Blob(["\uFEFF", csv], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = "account_billing_status.csv";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  },

  created() {
    this.loadReport();
  },

  beforeDestroy() {
    window.clearTimeout(this.searchTimer);
  },
};
</script>

<style scoped>
.account-billing-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 16px;
}

.account-billing-filters {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  flex: 1;
}

.account-billing-filter {
  width: 190px;
}

.account-billing-filter--search {
  width: min(360px, 100%);
}

.account-billing-filter label {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.account-billing-actions {
  display: flex;
  align-items: center;
}

.report-sort-head {
  cursor: pointer;
  user-select: none;
  color: inherit;
  text-decoration: none;
}

.report-sort-head:hover,
.report-sort-head:focus {
  color: inherit;
  text-decoration: none;
  outline: none;
}

.billing-status-badge {
  display: inline-block;
  min-width: 92px;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 600;
  text-align: center;
}

.billing-status-badge--paid {
  color: #0f5132;
  background-color: #d1e7dd;
}

.billing-status-badge--free {
  color: #084298;
  background-color: #cfe2ff;
}

.billing-status-badge--in_default {
  color: #842029;
  background-color: #f8d7da;
}

.billing-status-badge--unknown {
  color: #6c757d;
  background-color: #e9ecef;
}

@media (max-width: 767px) {
  .account-billing-filter,
  .account-billing-filter--search {
    width: 100%;
  }

  .account-billing-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

.company-name-cell {
  width: 320px;
  max-width: 320px;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.35;
}

::v-deep .company-name-column {
  width: 320px;
  min-width: 320px;
  max-width: 320px;
  white-space: normal;
}
</style>
